import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PRIORITY, fmtDateTime } from "@/lib/checkins";
import { ResolveDialog, errMsg } from "./ResolveDialog";

const STATUS_TXT = { open: "Not acknowledged", acknowledged: "Acknowledged", resolved: "Resolved" };

export function PriorityBadge({ severity }) {
  return <span className={`rounded px-1.5 py-0.5 text-2xs font-bold uppercase tracking-wide ${PRIORITY[severity] || PRIORITY.routine}`}>{severity}</span>;
}

// One alert/task with acknowledge, assign and resolve controls.
export function AlertRow({ alert, staff, onChanged, onOpenSubmission, highlight, readOnly, showClient = true }) {
  const [resolving, setResolving] = useState(false);
  const who = (id) => staff.find((u) => u.id === id)?.name || "Unassigned";
  async function act(path, body) {
    try {
      const r = await api.post(`/checkin-alerts/${alert.id}/${path}`, body || {});
      onChanged(r.data);
    } catch (e) {
      toast.error("Action failed", { description: errMsg(e) });
    }
  }
  const resolved = alert.status === "resolved";
  return (
    <div className={`rounded-lg border p-3 transition-colors duration-200 ${highlight ? "border-sky-400 bg-sky-50" : "border-zinc-200 bg-white"} ${resolved ? "opacity-70" : ""}`} data-testid={`checkin-alert-${alert.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <PriorityBadge severity={alert.severity} />
        <span className="font-semibold text-zinc-950">{alert.category}</span>
        {showClient && (
          <Link to={`/clients/${encodeURIComponent(alert.client_email)}?tab=checkins`} className="text-sm text-sky-800 underline-offset-2 hover:underline" data-testid={`checkin-alert-client-${alert.id}`}>
            {alert.client_name || alert.identifier}
          </Link>
        )}
        <span className="text-xs text-zinc-500">{fmtDateTime(alert.created_at)}</span>
        <span className={`ml-auto text-xs font-semibold ${alert.overdue_ack ? "text-red-700" : "text-zinc-600"}`} data-testid={`checkin-alert-status-${alert.id}`}>
          {alert.overdue_ack ? "Acknowledgement overdue" : STATUS_TXT[alert.status]}
          {alert.escalated_at && " · escalated to Owner"}
        </span>
      </div>
      {alert.reasons?.length > 0 && <ul className="mt-1.5 list-disc pl-5 text-sm text-zinc-700">{alert.reasons.map((r) => <li key={r}>{r}</li>)}</ul>}
      {resolved && <p className="mt-1 text-xs text-zinc-600">Outcome: {alert.resolution_code?.replace(/_/g, " ")}{alert.resolution_note ? ` — ${alert.resolution_note}` : ""}</p>}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {!readOnly && alert.status === "open" && <Button size="sm" onClick={() => act("acknowledge")} data-testid={`checkin-alert-ack-${alert.id}`}>Acknowledge</Button>}
        {!readOnly && !resolved && (
          <Select value={alert.assigned_to || ""} onValueChange={(v) => act("assign", { user_id: v })}>
            <SelectTrigger className="h-8 w-48 text-xs" data-testid={`checkin-alert-assign-${alert.id}`}><SelectValue placeholder="Assign to…" /></SelectTrigger>
            <SelectContent>{staff.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {(readOnly || resolved) && <span className="text-xs text-zinc-600">Assigned: {who(alert.assigned_to)}</span>}
        {!readOnly && !resolved && <Button size="sm" variant="outline" onClick={() => setResolving(true)} data-testid={`checkin-alert-resolve-${alert.id}`}>Resolve</Button>}
        {alert.submission_id && onOpenSubmission && (
          <Button size="sm" variant="ghost" onClick={() => onOpenSubmission(alert.submission_id)} data-testid={`checkin-alert-open-${alert.id}`}>Open progress review</Button>
        )}
      </div>
      {resolving && <ResolveDialog alert={alert} open onClose={() => setResolving(false)} onDone={onChanged} />}
    </div>
  );
}
